import { Y_MAP_EMBEDDED_MODULES } from '@/microfrontends/map/shared/constants/map';

import { FetchAssertsService } from '@/packages/query-state/shared/services/FetchAssertsService';
import { type IQueryStateAppContext } from '@/packages/query-state/shared/types/appContext';

export const SCRIPT_URL = 'https://enterprise.api-maps.yandex.ru/2.1/';

// Ключ неймспейса определен в типах window, там же описано, зачем он нам нужен
const windowNsKey = '_cianYMaps';

export function loadYandexMapScriptService(applicationContext: IQueryStateAppContext): Promise<YMaps.IYMaps> {
  const { logger, config, httpApi } = applicationContext;
  const fetchAssertsService = new FetchAssertsService(
    {},
    '@/shared/store/serverSide/yandex-map/v2.1/get-yandex-map/internal/fetchService.ts',
    logger,
  );

  const mapApiKey = config.getStrict<string>('audience.YandexApiKey');
  const suggestApiKey = config.getStrict<string>('audience.YandexSuggestApiKey');
  const ns = encodeURIComponent(mapApiKey);

  if (window[windowNsKey]?.[ns]) {
    return Promise.resolve(window[windowNsKey][ns]);
  }

  const srcUrl = new URL(SCRIPT_URL);
  const srcParams = new URLSearchParams({
    /* eslint-disable sort-keys */
    apikey: mapApiKey,
    suggest_apikey: suggestApiKey,
    lang: 'ru-RU',
    load: Y_MAP_EMBEDDED_MODULES.join(','),

    // Неймспейс, куда Яндекс инициализирует свои модули
    ns: `${windowNsKey}.${ns}`,

    // Имя функции, которая вызывается тогда, когда загруженный скрипт выполнился
    onload: 'onloadYMapScript',
    /* eslint-enable sort-keys */
  });

  const ymapsPromise = new Promise<YMaps.IYMaps>((resolve, reject) => {
    window.onloadYMapScript = function (ymaps: YMaps.IYMaps): void {
      resolve(ymaps);
    };

    httpApi
      .httpRequest({
        method: 'GET',
        uri: { host: srcUrl.host, path: srcUrl.pathname, query: srcParams.toString() },
        withCredentials: false,
      })
      .then(response => {
        const { responseBody } = response;
        const htmlScriptElement = window.document.createElement('script');

        htmlScriptElement.innerHTML = responseBody;
        htmlScriptElement.type = 'text/javascript';

        window.document.body.appendChild(htmlScriptElement);
      })
      .catch(error => {
        /**
         * Логируем через "warning" уровень, т.к. такие ошибки выбрасываются при неудачной загрузке скрипта.
         * Это случается при парсинге страницы поисковыми ботами, и мы не хотим захламлять логи
         * ненужными сообщениями об ошибках, которые нам ничего не дают.
         */
        reject(
          fetchAssertsService.assertUnknownServerError(error, {
            errorMessage: '[Yandex Maps] Ошибка при загрузке скрипта Яндекс Карт',
            logLevel: 'warning',
          }),
        );
      });
  });

  return ymapsPromise;
}
